import React, { useRef, useState } from 'react'
import { Form as FinalForm } from 'react-final-form'

import { FORM } from '../../utils/dataTrackingValues'
import Modal from '../Modal' // Don't want the ModalWithState as we will create our own
import Typography from '../Typography'
import DetermineFieldType from './DetermineFieldType'

import FormFields from './FormFields'
import FormSteps from './FormSteps'
import SubmissionMessage from './SubmissionMessage'
import useSteps from './useSteps'

import getInitialValue from './utils'

import * as Styled from './Form.styles'

const Form = ({
  addressQualificationCode = null,
  customSubmissionMessage = null,
  disclaimer = null,
  emilyTCPAUpdate = () => {},
  fields = null,
  formDisclaimerPosition = 'Below Submit Button',
  formId = null,
  formName = null,
  formSource = 'eloqua',
  heading = null,
  initialStepValues = {},
  inlineButton = false,
  isMultiStep = false,
  onSubmit = () => {},
  smartyStreetsWebsiteKey = null,
  spanish = false,
  steps = null,
  submissionData = {},
  subheading = null,
  submitButtonColor = 'primary',
  submitButtonGradient = false,
  submitButtonOutlined = false,
  submitButtonText = 'Submit',
  tcpaCheckbox = false,
}) => {
  const [showSubmissionMessage, setShowSubmissionMessage] = useState(false)
  const [showSubmissionErrorMessage, setShowSubmissionErrorMessage] =
    useState(false)
  const { nextStep, currentStep, stepValues } = useSteps(
    steps,
    initialStepValues,
    0
  )
  const formRef = useRef(null)

  // Every form will have these fields.
  const { sessionPromoCode, requestId, siteURL, brandToken } = submissionData
  const initialValues = {
    promo_code: sessionPromoCode,
    request_id: requestId,
    source: formName,
    domain: siteURL,
    brand: brandToken,
  }

  if (fields) {
    fields.forEach((field) => {
      /** We need to first check if we have default values for the brand before falling back to brandToken. */
      if (field.name === 'brand' && (field.defaultValue || field.value)) {
        initialValues.brand = field.defaultValue || field.value
      } else {
        initialValues[field.name] = getInitialValue(initialValues, field)
      }
    })
  }

  if (steps) {
    steps.forEach((step) => {
      step.formFields.forEach((field) => {
        initialValues[field.name] = getInitialValue(initialValues, field)
      })
    })
  }

  if (showSubmissionMessage) {
    return customSubmissionMessage || <SubmissionMessage formId={formId} />
  }

  /**
   * The onSubmit function needs to be asynchronous because react final form
   * uses that to know that the form is currently submitting
   * while it waits for the form submission to complete.
   * Here we combine the data we got from each step if the form is
   * multi step and combine it with the regular values.
   *
   * NOTE: Prettier and Eslint can't get along if this function is
   * created inside the `onSubmit` prop.
   */
  const finalFormOnSubmit = (values, bag) => {
    FORM.handleClick(values, requestId)
    Promise.resolve(
      onSubmit({
        formRef: formRef.current,
        values: { ...values, ...stepValues },
        bag,
        setShowSubmissionMessage,
        setShowSubmissionErrorMessage,
        smartyStreetsWebsiteKey,
        addressQualificationCode,
        tcpaCheckbox,
      })
    )
  }

  return (
    <Styled.FormContainer className="leshen-form" ref={formRef}>
      {heading && (
        <Typography className="heading" variant="h3">
          {heading}
        </Typography>
      )}
      {subheading && (
        <Typography className="subheading" variant="h6">
          {subheading}
        </Typography>
      )}
      <FinalForm
        className="final-form"
        initialValues={initialValues}
        onSubmit={finalFormOnSubmit}
      >
        {({ handleSubmit, submitting }) => {
          // We create the submit button ahead of time because sometimes it has to be
          // passed to DetermineFieldType if it needs to be inline with an input, otherwise
          // we render it at the bottom of the form.
          const submitButton = (
            <Styled.SubmitButton
              className="submit-button"
              type="submit"
              disabled={submitting}
              color={submitButtonColor}
              outlined={submitButtonOutlined}
              gradient={submitButtonGradient}
            >
              {submitButtonText}
            </Styled.SubmitButton>
          )

          return (
            <>
              <Modal
                isShowing={showSubmissionErrorMessage}
                onClose={() => setShowSubmissionErrorMessage(false)}
                size="sm"
              >
                <h1>
                  {spanish
                    ? 'Parece que hay un problema al enviar su formulario.'
                    : 'Looks like there’s an issue submitting your form.'}
                </h1>
                <Typography>
                  {spanish
                    ? 'Asegúrese de que su bloqueador de anuncios esté desactivado y vuelva a intentarlo o llámenos para completar su pedido.'
                    : 'Ensure your ad-blocker is disabled and submit again, or call us to complete your order!'}
                </Typography>
              </Modal>
              <form
                noValidate
                data-testid="Form-form"
                data-form-type={formSource}
                onSubmit={handleSubmit}
                name={formName}
                className="form"
              >
                {isMultiStep ? (
                  <FormSteps
                    steps={steps}
                    submitButton={submitButton}
                    currentStep={currentStep}
                    nextStep={nextStep}
                    formName={formName}
                    disclaimer={disclaimer}
                    tcpaCheckbox={tcpaCheckbox}
                    formDisclaimerPosition={formDisclaimerPosition}
                    smartyStreetsWebsiteKey={smartyStreetsWebsiteKey}
                  />
                ) : (
                  <FormFields
                    className={formName}
                    inlineButton={inlineButton}
                    submitButton={submitButton}
                    tcpaCheckbox={tcpaCheckbox}
                    formDisclaimerPosition={formDisclaimerPosition}
                    fields={fields}
                    smartyStreetsWebsiteKey={smartyStreetsWebsiteKey}
                    additional={
                      <>
                        <Styled.TCPADisclaimerContainer>
                          {tcpaCheckbox &&
                            formDisclaimerPosition ===
                              'Above Submit Button' && (
                              <DetermineFieldType
                                submitButton={submitButton}
                                onChange={emilyTCPAUpdate}
                                formRef={formRef}
                                field={{
                                  type: 'checkbox',
                                  element: 'checkbox',
                                  name: 'tcpa-checkbox',
                                  required: true,
                                  options: [
                                    {
                                      name: 'consent',
                                      value: 'yes',
                                      label: '',
                                    },
                                  ],
                                }}
                              />
                            )}
                          {disclaimer &&
                            formDisclaimerPosition ===
                              'Above Submit Button' && (
                              <Styled.FormDisclaimer
                                disclaimer={disclaimer?.brandy}
                              />
                            )}
                        </Styled.TCPADisclaimerContainer>

                        {!inlineButton && submitButton}

                        <Styled.TCPADisclaimerContainer>
                          {tcpaCheckbox &&
                            formDisclaimerPosition !==
                              'Above Submit Button' && (
                              <DetermineFieldType
                                submitButton={submitButton}
                                onChange={emilyTCPAUpdate}
                                formRef={formRef}
                                field={{
                                  type: 'checkbox',
                                  element: 'checkbox',
                                  name: 'tcpa-checkbox',
                                  required: true,
                                  options: [
                                    {
                                      name: 'consent',
                                      value: 'yes',
                                      label: '',
                                    },
                                  ],
                                }}
                              />
                            )}
                          {disclaimer &&
                            formDisclaimerPosition !==
                              'Above Submit Button' && (
                              <Styled.FormDisclaimer
                                {...disclaimer?.brandy}
                                disclaimer={disclaimer?.brandy}
                              />
                            )}
                        </Styled.TCPADisclaimerContainer>
                      </>
                    }
                  />
                )}
              </form>
            </>
          )
        }}
      </FinalForm>
    </Styled.FormContainer>
  )
}

export default Form
